import { send_invite_event } from "@/services/posthog/events";
import {
  Button,
  CloseButton,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  useToast,
} from "@chakra-ui/react";
import { AxiosError } from "axios";
import { useState } from "react";
import { sendEmailInvite } from "../../actions/sendEmailInvite";
import { useAnalytics } from "@/services/posthog/hooks";

interface InviteMemberModalProps {
  isOpen: boolean;
  onClose: () => void;
  teamId: string;
}

const isValidEmail = (email: string) => {
  return !!email.match(
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
  );
};

export const InviteMemberModal = (props: InviteMemberModalProps) => {
  const [email, setEmail] = useState("");
  const { isOpen, onClose, teamId } = props;
  const [isSending, setIsSending] = useState(false);
  const toast = useToast();
  const analytics = useAnalytics();

  const showSendInviteError = (err: AxiosError) => {
    toast({
      title: "Error Sending Invite",
      description: (err.response?.data as string) || err.message,
      status: "error",
      duration: 5000,
      isClosable: true,
      position: "top",
    });
  };

  const showSendInviteConfirmation = () => {
    toast({
      title: "Invite sent!",
      description: "Your invite has been sent to the email address you provided.",
      status: "success",
      duration: 5000,
      isClosable: true,
      position: "top",
    });
  };

  const sendEmail = async (email: string) => {
    if (email && isValidEmail(email)) {
      setIsSending(true);
      analytics.capture(send_invite_event);
      const { error } = await sendEmailInvite(email, teamId);
      if (error) {
        showSendInviteError(error);
      } else {
        showSendInviteConfirmation();
      }
      setIsSending(false);
      onClose();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Text as="h3" textStyle="label/L/medium">
            Add team members
          </Text>
          <CloseButton variant="ghost" onClick={onClose} />
        </ModalHeader>
        <ModalBody>
          <Stack gap="0.5" flex="1">
            <Text textStyle="label/S/medium" color="foreground.secondary">
              Send invitation
            </Text>
            <Stack direction="row" gap="0">
              <Input
                size="lg"
                placeholder="email address"
                mr="0.5rem"
                isInvalid={!!email && !isValidEmail(email)}
                onChange={(e) => {
                  setEmail(e.target.value);
                }}
                onKeyDown={async (e) => {
                  if (e.key === "Enter" && isValidEmail(email)) {
                    sendEmail(email);
                  }
                }}
              />
              <Button
                size="md"
                variant="primary"
                minWidth="80px"
                isDisabled={!email || !isValidEmail(email)}
                isLoading={isSending}
                onClick={async () => {
                  if (email && isValidEmail(email)) {
                    sendEmail(email);
                  }
                }}
              >
                Invite
              </Button>
            </Stack>
          </Stack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
